// Account (WEB_PLAN.md §4.7, route /account): who is signed in, appearance (System / Light / Dark), change password,
// sign out of this browser, leave the company, delete the account. Mirrors iOS Settings › Team Sync (TeamSyncView + AccountActionsCard):
// - Before Leave / Delete, `team_roster` tells whether the caller is the only member (iOS `isOnlyMember()`): the
//   server then deletes the company and all its data, and the confirmation says so.
// - Both are behind a typed confirmation; errors stay in the dialog in the iOS wording.
// - Leaving re-reads memberships: the console goes to No company and this browser's copy is wiped.
// - Deleting signs this browser out and the sign-in screen says "Account deleted". A call that gets no answer (a
//   dropped connection; on the live site, every call until the functions are deployed with CORS, Appendix A item 1)
//   says to try again or use groundteam-app.netlify.app/delete-account (DELETE_NO_ANSWER_TEXT).

import { Building, LogOut, Trash, UserRoundX } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { deleteAccount, isOnlyMember, leaveCompany } from '@/auth/account';
import {
  ACCOUNT_DELETED_NOTICE,
  accountLabel,
  deleteAccountErrorMessage,
  deleteConfirmation,
  leaveConfirmation,
  ROLE_TITLES,
} from '@/auth/accountText';
import { signOutNow, useAuth, useSession } from '@/auth/AuthProvider';
import { teamErrorMessage } from '@/auth/errors';
import { UNNAMED_COMPANY } from '@/auth/memberships';
import { setSignedOutNotice } from '@/auth/pending';
import { isAdmin } from '@/session/types';
import { ThemeSwitcher } from '@/ui';
import { Caption, SecondaryButton } from '@/features/auth/components/ui';
import { ChangePasswordCard } from './components/ChangePasswordCard';
import { SignOutDialog } from './components/SignOutDialog';
import { TypedConfirmDialog } from './components/TypedConfirmDialog';

type Action = 'leave' | 'delete';

interface DialogState {
  action: Action;
  /** From team_roster: true = the caller is the only member; null = couldn't be checked. */
  onlyMember: boolean | null;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <h2 className="text-xs font-semibold tracking-wide text-slate-500 uppercase dark:text-slate-400">{title}</h2>
      {children}
    </section>
  );
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-slate-100 py-2 last:border-b-0 dark:border-slate-800">
      <dt className="text-sm text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="min-w-0 truncate text-right text-sm font-semibold text-slate-900 dark:text-slate-100">{value}</dd>
    </div>
  );
}

export default function AccountPage() {
  const session = useSession();
  const { user, refreshMemberships } = useAuth();
  const company = session.companyName;
  const account = accountLabel(user) ?? session.email;
  const admin = isAdmin(session);

  /** Checking the roster before a dialog opens. */
  const [checking, setChecking] = useState<Action | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [working, setWorking] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const busy = checking !== null || working;

  const ask = async (action: Action) => {
    if (busy) return;
    setChecking(action);
    const onlyMember = await isOnlyMember(session);
    setChecking(null);
    setDialogError(null);
    setDialog({ action, onlyMember });
  };

  const closeDialog = useCallback(() => {
    setDialog(null);
    setDialogError(null);
  }, []);

  const confirm = async () => {
    if (!dialog || working) return;
    setWorking(true);
    setDialogError(null);
    try {
      if (dialog.action === 'leave') {
        await leaveCompany(session);
        // The membership is gone: AuthProvider wipes this browser's copy and the console moves to No company. If
        // that check can't reach the server right now, sync's next call gets not_member and does the same.
        await refreshMemberships();
      } else {
        await deleteAccount();
        setSignedOutNotice(ACCOUNT_DELETED_NOTICE);
        // SIGNED_OUT wipes this browser's copy; the sign-in screen shows the notice.
        await signOutNow();
      }
      // Normally this page is gone by now; never leave the dialog spinning.
      setWorking(false);
      setDialog(null);
    } catch (e) {
      setDialogError(
        dialog.action === 'leave'
          ? teamErrorMessage(e, company)
          : deleteAccountErrorMessage(e, company, typeof navigator === 'undefined' || navigator.onLine !== false),
      );
      setWorking(false);
    }
  };

  const text = dialog
    ? dialog.action === 'leave'
      ? leaveConfirmation(dialog.onlyMember, company)
      : deleteConfirmation(dialog.onlyMember, company, account)
    : null;
  // Deleting the account is confirmed with a word; leaving with the company's name (a word when it has none).
  const confirmWord = dialog?.action === 'delete' ? 'DELETE' : company !== UNNAMED_COMPANY ? company : 'LEAVE';

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-6 sm:px-6">
      <header>
        <h1 className="text-2xl font-bold text-[var(--gt-navy)] dark:text-slate-50">Account</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Your sign-in and your place in {company}.</p>
      </header>

      <Section title="Your account">
        <dl>
          <InfoRow label="Signed in as" value={account ?? '—'} />
          <InfoRow
            label="Company"
            value={
              <span className="inline-flex items-center gap-1.5">
                <Building className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                {company}
              </span>
            }
          />
          <InfoRow label="Role" value={ROLE_TITLES[session.role]} />
        </dl>
      </Section>

      <Section title="Appearance">
        <ThemeSwitcher fullWidth />
        <Caption>System matches your computer's light or dark setting. Your choice is saved in this browser.</Caption>
      </Section>

      <Section title="Account">
        <ChangePasswordCard account={account} disabled={busy} />
        <SecondaryButton icon={<LogOut className="h-4 w-4" aria-hidden />} disabled={busy} onClick={() => setSigningOut(true)}>
          Sign out of this browser
        </SecondaryButton>
        <Caption>
          Signing out removes the company's data from this browser. Your account and the team's data stay on the server,
          and your iPhone stays signed in.
        </Caption>
        <div className="my-1 h-px bg-slate-200 dark:bg-slate-700" />
        <SecondaryButton
          tone="danger"
          icon={<UserRoundX className="h-4 w-4" aria-hidden />}
          busy={checking === 'leave' || (working && dialog?.action === 'leave')}
          busyLabel={checking === 'leave' ? 'Checking…' : 'Leaving…'}
          disabled={busy}
          onClick={() => void ask('leave')}
        >
          Leave company
        </SecondaryButton>
        <SecondaryButton
          tone="danger"
          icon={<Trash className="h-4 w-4" aria-hidden />}
          busy={checking === 'delete' || (working && dialog?.action === 'delete')}
          busyLabel={checking === 'delete' ? 'Checking…' : 'Deleting…'}
          disabled={busy}
          onClick={() => void ask('delete')}
        >
          Delete my account
        </SecondaryButton>
        <Caption>
          {admin
            ? "If you're the only admin, make someone else an admin before leaving or deleting your account."
            : 'Deleting your account removes your sign-in permanently.'}
        </Caption>
      </Section>

      {dialog && text && (
        <TypedConfirmDialog
          title={text.title}
          message={text.message}
          confirmWord={confirmWord}
          confirmLabel={text.confirmLabel}
          busy={working}
          busyLabel={dialog.action === 'leave' ? 'Leaving…' : 'Deleting…'}
          error={dialogError}
          errorTitle={dialog.action === 'leave' ? `Couldn't leave ${company}` : "Couldn't delete your account"}
          onConfirm={() => void confirm()}
          onCancel={closeDialog}
        />
      )}
      {signingOut && <SignOutDialog companyName={company} onClose={() => setSigningOut(false)} />}
    </div>
  );
}
